import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import UserManagement from "./UserManagement";
import "./UserManagement.css";
import "./AdminPanel.css";

const API_BASE = "/api";

/*
 * Staff screens for client accounts: list, add, edit, suspend, their
 * numbers, their users and their history. Only shown to super admins;
 * the API refuses everyone else.
 */

const EMPTY_ACCOUNT = {
  name: "",
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  notes: "",
};

const ACTION_LABELS = {
  "account.create": "Client added",
  "account.update": "Client details changed",
  "account.suspend": "Client suspended",
  "account.reactivate": "Client reactivated",
  "account.staff_access": "10 Digits staff opened this account",
  "auth.login": "Signed in",
  "auth.login_failed": "Failed sign-in",
  "auth.mfa_enabled": "Two-step login set up",
  "auth.password_set_from_invite": "Accepted invite",
  "auth.password_set_from_password_reset": "Reset password from link",
  "user.create": "User added",
  "user.invite": "User invited",
  "user.update": "User changed",
  "user.deactivate": "User deactivated",
  "user.reactivate": "User reactivated",
  "user.password_set": "Password set by admin",
  "user.link_invite": "Invite link made",
  "user.link_password_reset": "Reset link made",
  "user.mfa_reset": "Two-step login reset",
  "number.register": "Number added",
  "number.update": "Number changed",
  "number.release": "Number released",
  "number.restore": "Number restored",
  "conversation.assign": "Conversation reassigned",
  "conversation.delete": "Conversation deleted",
  "message.inbound_dropped": "Incoming text not delivered",
};

function formatPhone(number) {
  const digits = String(number || "").replace(/\D/g, "");
  const local = digits.length === 11 && digits[0] === "1" ? digits.slice(1) : digits;

  return local.length === 10
    ? `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`
    : number;
}

function plural(count, word) {
  return `${count} ${word}${count === 1 ? "" : "s"}`;
}

function errorText(error, fallback) {
  return error.response?.data?.error || fallback;
}

function StatusBadge({ status }) {
  const label = {
    active: "Active",
    suspended: "Suspended",
    released: "Released",
  }[status] || status;

  return (
    <span className={`user-status admin-status-${status}`}>{label}</span>
  );
}

function AccountForm({ initial, saving, submitLabel, onSubmit, onCancel }) {
  const [form, setForm] = useState({ ...EMPTY_ACCOUNT, ...initial });

  const update = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  return (
    <form
      className="user-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(form);
      }}
    >
      <div className="user-form-grid">
        <label className="user-form-wide">
          Client name
          <input name="name" value={form.name} onChange={update} maxLength="200" required />
        </label>
        <label>
          Contact person
          <input name="contactName" value={form.contactName || ""} onChange={update} maxLength="200" />
        </label>
        <label>
          Contact phone
          <input name="contactPhone" value={form.contactPhone || ""} onChange={update} maxLength="40" />
        </label>
        <label className="user-form-wide">
          Contact email
          <input type="email" name="contactEmail" value={form.contactEmail || ""} onChange={update} maxLength="254" />
        </label>
        <label className="user-form-wide">
          Notes (only 10 Digits staff see these)
          <textarea name="notes" value={form.notes || ""} onChange={update} maxLength="2000" rows="3" />
        </label>
      </div>
      <div className="user-form-actions">
        {onCancel && (
          <button type="button" className="user-secondary-button" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button type="submit" className="user-primary-button" disabled={saving}>
          {saving ? "Saving..." : submitLabel}
        </button>
      </div>
    </form>
  );
}

function ClientList({ accounts, loading, homeAccountId, onOpen, onCreated, headers }) {
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();

    return accounts
      .filter(
        (account) =>
          !term ||
          [account.name, account.contactName, account.contactEmail]
            .filter(Boolean)
            .some((value) => value.toLowerCase().includes(term))
      )
      // Staff's own account first, then clients by name.
      .sort(
        (x, y) =>
          (y.id === homeAccountId) - (x.id === homeAccountId) ||
          x.name.localeCompare(y.name)
      );
  }, [accounts, search, homeAccountId]);

  const create = async (form) => {
    setSaving(true);
    setError("");

    try {
      const response = await axios.post(`${API_BASE}/accounts`, form, { headers });
      setShowForm(false);
      onCreated(response.data.account);
    } catch (requestError) {
      setError(errorText(requestError, "Unable to add the client."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="user-modal-toolbar">
        <input
          className="admin-search"
          type="search"
          placeholder="Search clients"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          aria-label="Search clients"
        />
        <button type="button" className="user-primary-button" onClick={() => setShowForm(true)}>
          + Add client
        </button>
      </div>

      {error && <div className="user-alert user-alert-error">{error}</div>}

      {showForm && (
        <AccountForm
          initial={EMPTY_ACCOUNT}
          saving={saving}
          submitLabel="Add client"
          onSubmit={create}
          onCancel={() => setShowForm(false)}
        />
      )}

      <div className="user-list">
        {loading ? (
          <div className="user-list-message">Loading clients...</div>
        ) : visible.length === 0 ? (
          <div className="user-list-message">
            {search ? "No clients match your search." : "No clients yet."}
          </div>
        ) : (
          visible.map((account) => (
            <button
              type="button"
              key={account.id}
              className={`admin-client-row ${account.status === "suspended" ? "user-row-inactive" : ""}`}
              onClick={() => onOpen(account.id)}
            >
              <span className="admin-client-name">
                <strong>
                  {account.name}
                  {account.id === homeAccountId && " (your account)"}
                </strong>
                <span>{account.contactName || account.contactEmail || "No contact yet"}</span>
              </span>
              <span className="admin-client-stats">
                <span>{plural(account._count?.users ?? 0, "user")}</span>
                <span>{plural(account._count?.phoneNumbers ?? 0, "number")}</span>
                <span>
                  {plural((account.usage?.inbound ?? 0) + (account.usage?.outbound ?? 0), "text")} in{" "}
                  {account.usage?.days ?? 30} days
                </span>
              </span>
              <StatusBadge status={account.status} />
            </button>
          ))
        )}
      </div>
    </>
  );
}

function NumbersSection({ account, headers, users, canChange }) {
  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newNumber, setNewNumber] = useState({ number: "", label: "" });
  const [adding, setAdding] = useState(false);

  const scoped = useMemo(() => ({ ...headers, "X-Account-Id": account.id }), [headers, account.id]);

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const response = await axios.get(`${API_BASE}/phone-numbers`, { headers: scoped });
      setNumbers(response.data?.phoneNumbers || []);
    } catch (requestError) {
      setError(errorText(requestError, "Unable to load numbers."));
    } finally {
      setLoading(false);
    }
  }, [scoped]);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (phoneNumber, changes, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) {
      return;
    }

    setError("");

    try {
      await axios.patch(`${API_BASE}/phone-numbers/${phoneNumber.id}`, changes, { headers: scoped });
      await load();
    } catch (requestError) {
      setError(errorText(requestError, "Unable to update the number."));
    }
  };

  const add = async (event) => {
    event.preventDefault();
    setAdding(true);
    setError("");

    try {
      await axios.post(`${API_BASE}/phone-numbers`, newNumber, { headers: scoped });
      setNewNumber({ number: "", label: "" });
      await load();
    } catch (requestError) {
      setError(errorText(requestError, "Unable to add the number."));
    } finally {
      setAdding(false);
    }
  };

  const rename = (phoneNumber) => {
    const label = window.prompt("Name for this number", phoneNumber.label || "");

    if (label !== null) {
      update(phoneNumber, { label });
    }
  };

  return (
    <section className="admin-section">
      <h3>Numbers</h3>
      {error && <div className="user-alert user-alert-error">{error}</div>}

      {loading ? (
        <div className="user-list-message">Loading numbers...</div>
      ) : numbers.length === 0 ? (
        <div className="user-list-message">No numbers yet.</div>
      ) : (
        numbers.map((phoneNumber) => {
          const released = phoneNumber.status === "released";

          return (
            <div className={`admin-number-row ${released ? "user-row-inactive" : ""}`} key={phoneNumber.id}>
              <span className="admin-client-name">
                <strong>{formatPhone(phoneNumber.number)}</strong>
                <span>{phoneNumber.label || "No name"}</span>
              </span>
              <select
                value={phoneNumber.assignedToUserId || ""}
                onChange={(event) =>
                  update(phoneNumber, { assignedToUserId: event.target.value || null })
                }
                disabled={released || !canChange}
                aria-label="Who this number belongs to"
              >
                <option value="">Shared (no one)</option>
                {users
                  .filter(
                    (user) =>
                      ["active", "invited"].includes(user.status) ||
                      user.id === phoneNumber.assignedToUserId
                  )
                  .map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                      {user.status === "invited" ? " (invited)" : ""}
                    </option>
                  ))}
              </select>
              <StatusBadge status={phoneNumber.status} />
              <span className="user-row-actions">
                <button type="button" className="user-secondary-button" onClick={() => rename(phoneNumber)} disabled={!canChange}>
                  Rename
                </button>
                <button
                  type="button"
                  className={released ? "user-secondary-button" : "user-danger-button"}
                  onClick={() =>
                    released
                      ? update(phoneNumber, { status: "active" })
                      : update(
                          phoneNumber,
                          { status: "released" },
                          `Release ${formatPhone(phoneNumber.number)}? It will stop sending, and texts to it will no longer arrive.`
                        )
                  }
                >
                  {released ? "Restore" : "Release"}
                </button>
              </span>
            </div>
          );
        })
      )}

      {canChange && (
        <form className="admin-inline-form" onSubmit={add}>
          <input
            placeholder="New number, e.g. (512) 555-0100"
            value={newNumber.number}
            onChange={(event) => setNewNumber((current) => ({ ...current, number: event.target.value }))}
            required
            aria-label="New number"
          />
          <input
            placeholder="Name (optional)"
            value={newNumber.label}
            onChange={(event) => setNewNumber((current) => ({ ...current, label: event.target.value }))}
            maxLength="100"
            aria-label="Name for the new number"
          />
          <button type="submit" className="user-primary-button" disabled={adding}>
            {adding ? "Adding..." : "Add number"}
          </button>
        </form>
      )}
      {canChange && (
        <p className="admin-hint">
          Add a number here only after it's set up in Telnyx. Texts to it will then arrive in this client's inbox.
        </p>
      )}
    </section>
  );
}

function HistorySection({ account, headers }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(
    async (before) => {
      setLoading(true);

      try {
        const response = await axios.get(`${API_BASE}/accounts/${account.id}/audit`, {
          headers,
          params: { limit: 25, ...(before ? { before } : {}) },
        });
        const page = response.data?.entries || [];
        setEntries((current) => (before ? [...current, ...page] : page));
        setDone(page.length < 25);
      } catch (requestError) {
        setError(errorText(requestError, "Unable to load history."));
      } finally {
        setLoading(false);
      }
    },
    [account.id, headers]
  );

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section className="admin-section">
      <h3>History</h3>
      {error && <div className="user-alert user-alert-error">{error}</div>}
      {entries.length === 0 && !loading ? (
        <div className="user-list-message">Nothing recorded yet.</div>
      ) : (
        <ul className="admin-history">
          {entries.map((entry) => (
            <li key={entry.id}>
              <time dateTime={entry.createdAt}>{new Date(entry.createdAt).toLocaleString()}</time>
              <strong>{ACTION_LABELS[entry.action] || entry.action}</strong>
              <span>{entry.actorEmail || "System"}</span>
            </li>
          ))}
        </ul>
      )}
      {!done && (
        <button
          type="button"
          className="user-secondary-button"
          onClick={() => load(entries[entries.length - 1]?.createdAt)}
          disabled={loading}
        >
          {loading ? "Loading..." : "Show older"}
        </button>
      )}
    </section>
  );
}

function ClientDetail({ accountId, token, headers, currentUser, onBack, onChanged }) {
  const isHomeAccount = accountId === currentUser?.accountId;
  const [account, setAccount] = useState(null);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showUsers, setShowUsers] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);

  const load = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/accounts/${accountId}`, { headers });
      setAccount(response.data.account);

      if (response.data.account.status === "active") {
        const usersResponse = await axios.get(`${API_BASE}/users`, {
          headers: { ...headers, "X-Account-Id": accountId },
        });
        setUsers(usersResponse.data?.users || []);
      }
    } catch (requestError) {
      setError(errorText(requestError, "Unable to load this client."));
    }
  }, [accountId, headers]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (changes, message) => {
    setSaving(true);
    setError("");
    setNotice("");

    try {
      const response = await axios.patch(`${API_BASE}/accounts/${accountId}`, changes, { headers });
      setAccount((current) => ({ ...current, ...response.data.account }));
      setEditing(false);
      setNotice(message);
      setHistoryKey((key) => key + 1);
      onChanged();
      return true;
    } catch (requestError) {
      setError(errorText(requestError, "Unable to save."));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const toggleSuspended = async () => {
    const suspend = account.status === "active";

    if (
      suspend &&
      !window.confirm(
        `Suspend ${account.name}? Their users are signed out and can't sign in or send, and new texts to their numbers won't arrive. Nothing is deleted.`
      )
    ) {
      return;
    }

    if (await save({ status: suspend ? "suspended" : "active" }, suspend ? "Client suspended." : "Client reactivated.")) {
      load();
    }
  };

  if (!account) {
    return (
      <div className="user-list">
        {error ? <div className="user-alert user-alert-error">{error}</div> : <div className="user-list-message">Loading...</div>}
      </div>
    );
  }

  const active = account.status === "active";

  return (
    <>
      <div className="user-modal-toolbar">
        <button type="button" className="user-secondary-button" onClick={onBack}>
          ← All clients
        </button>
        <span className="admin-toolbar-title">
          <strong>{account.name}</strong>
          <StatusBadge status={account.status} />
        </span>
      </div>

      <div className="user-list admin-detail">
        {error && <div className="user-alert user-alert-error">{error}</div>}
        {notice && <div className="user-alert user-alert-success">{notice}</div>}

        <section className="admin-section">
          <h3>Details</h3>
          {editing ? (
            <AccountForm
              initial={account}
              saving={saving}
              submitLabel="Save changes"
              onSubmit={(form) => save(form, "Details saved.")}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <dl className="admin-details">
              <dt>Contact</dt>
              <dd>{account.contactName || "—"}</dd>
              <dt>Email</dt>
              <dd>{account.contactEmail || "—"}</dd>
              <dt>Phone</dt>
              <dd>{account.contactPhone || "—"}</dd>
              <dt>Notes</dt>
              <dd className="admin-notes">{account.notes || "—"}</dd>
              <dt>Added</dt>
              <dd>{new Date(account.createdAt).toLocaleDateString()}</dd>
              {account.suspendedAt && (
                <>
                  <dt>Suspended</dt>
                  <dd>{new Date(account.suspendedAt).toLocaleString()}</dd>
                </>
              )}
            </dl>
          )}
          {!editing && (
            <div className="user-row-actions admin-actions">
              <button type="button" className="user-secondary-button" onClick={() => setEditing(true)}>
                Edit details
              </button>
              <button
                type="button"
                className="user-secondary-button"
                onClick={() => setShowUsers(true)}
                disabled={!active}
                title={active ? "" : "Reactivate the client to manage its users"}
              >
                Manage users ({users.length})
              </button>
              {!isHomeAccount && (
                <button
                  type="button"
                  className={active ? "user-danger-button" : "user-primary-button"}
                  onClick={toggleSuspended}
                  disabled={saving}
                >
                  {active ? "Suspend client" : "Reactivate client"}
                </button>
              )}
            </div>
          )}
        </section>

        {active ? (
          <NumbersSection account={account} headers={headers} users={users} canChange />
        ) : (
          <section className="admin-section">
            <h3>Numbers</h3>
            <p className="admin-hint">Reactivate the client to see and change its numbers.</p>
          </section>
        )}

        <HistorySection key={historyKey} account={account} headers={headers} />
      </div>

      {showUsers && (
        <UserManagement
          token={token}
          currentUser={currentUser}
          accountId={account.id}
          accountName={account.name}
          onClose={() => {
            setShowUsers(false);
            setHistoryKey((key) => key + 1);
            load();
          }}
          onCurrentUserUpdated={() => {}}
        />
      )}
    </>
  );
}

function AdminPanel({ token, currentUser, onClose, onAccountsChanged }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openAccountId, setOpenAccountId] = useState("");

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadAccounts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/accounts`, { headers });
      setAccounts(response.data?.accounts || []);
    } catch (requestError) {
      setError(errorText(requestError, "Unable to load clients."));
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const changed = () => {
    loadAccounts();
    onAccountsChanged();
  };

  return (
    <div className="user-modal-backdrop" role="presentation">
      <section className="user-modal admin-modal" role="dialog" aria-modal="true" aria-labelledby="admin-title">
        <header className="user-modal-header">
          <div>
            <h2 id="admin-title">Clients</h2>
            <p>10 Digits staff only. Every change here is recorded in the client's history.</p>
          </div>
          <button type="button" className="user-modal-close" onClick={onClose} aria-label="Close clients">
            ×
          </button>
        </header>

        {error && <div className="user-alert user-alert-error">{error}</div>}

        {openAccountId ? (
          <ClientDetail
            accountId={openAccountId}
            token={token}
            headers={headers}
            currentUser={currentUser}
            onBack={() => setOpenAccountId("")}
            onChanged={changed}
          />
        ) : (
          <ClientList
            accounts={accounts}
            loading={loading}
            homeAccountId={currentUser?.accountId}
            headers={headers}
            onOpen={setOpenAccountId}
            onCreated={(account) => {
              changed();
              setOpenAccountId(account.id);
            }}
          />
        )}
      </section>
    </div>
  );
}

export default AdminPanel;
